import { useEffect, useRef, useState } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset, kuiperClassNames } from '@opallabs/kuiper'
import {
  Module,
  OnDragDropProps,
  OnShowAssetPickerProps,
} from '@opallabs/periodic/src/Content/helpers/types'
import { SingleAssetPicker } from '@opallabs/periodic/src/SingleAssetPicker'
import { doNothing } from '../../../helpers/functions/doNothing'
import { MobileStreamHeader, MobileStreamFooter } from '../../sharedDisplays'
import { styles } from './styles'

export type DisplayProps = {
  accountImage: AssetReference | null
  accountUsername: string
  assetHeight: number
  assets: AssetReference[]
  collabAccount: string
  content: string
  index: number
  instagramCollab: boolean
  isDragging: boolean
  isExpanded: boolean
  isPresenting: boolean
  location: string
  modules: Module[]
  notCropped: boolean
  onContentChange: (val: string) => void
  onDragDrop: OnDragDropProps
  onExpandAsset: (id: number) => void
  onFooterResize: (footerHeight: number) => void
  onRemoveAsset: (id: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  postType: string
  readOnly: boolean
  truncate: boolean
}

export const MobileStream: React.FC<DisplayProps> = ({
  accountImage,
  accountUsername,
  assetHeight,
  assets,
  collabAccount,
  content,
  index,
  instagramCollab,
  isDragging,
  isPresenting,
  location,
  modules,
  notCropped,
  onContentChange,
  onDragDrop,
  onExpandAsset,
  onFooterResize,
  onRemoveAsset,
  onShowAssetPicker,
  postType,
  readOnly,
  truncate,
}) => {
  const asset = assets && assets.length ? assets[0] : null
  const [isHovering, setIsHovering] = useState(false)
  const footerRef = useRef<HTMLDivElement>(null)
  const residualHeight = useRef(0)
  const truncateRef = useRef(truncate)

  useEffect(() => {
    // ref height returns twice for some reason, we're avoiding
    // getting the height that is not accurate
    if (
      footerRef.current?.clientHeight &&
      footerRef.current?.clientHeight !== residualHeight.current
    ) {
      if (!residualHeight.current || truncateRef.current !== truncate)
        residualHeight.current = footerRef.current?.clientHeight
      onFooterResize(footerRef.current?.clientHeight)
    }
    truncateRef.current = truncate
  }, [
    content,
    footerRef.current?.clientHeight,
    modules,
    notCropped,
    onFooterResize,
    truncate,
  ])

  return (
    <div
      className={kuiperClassNames('mobileStream-carousel')}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <MobileStreamHeader
        accountUsername={accountUsername}
        accountImage={accountImage}
        musicTitle=""
        instagramCollab={instagramCollab}
        collabAccount={collabAccount}
        postType={postType}
        readOnly={readOnly}
        location={location}
        onMusicChange={doNothing}
      />
      <div>
        {asset ? (
          <div>
            <Asset
              imageFit="cover"
              assetReference={asset}
              placeholder={false}
              isHovered={isHovering}
              hasAssetOverlay={asset && (!readOnly || isPresenting)}
              removeable={!readOnly}
              overrideStyles={{
                asset: styles.assetStyles(notCropped, assetHeight),
                assetBase: styles.assetStyles(notCropped, assetHeight),
              }}
              onEmbiggen={() => onExpandAsset(asset.id || 0)}
              onRemove={() => onRemoveAsset(asset.id || 0, index)}
            />
          </div>
        ) : (
          <SingleAssetPicker
            allowedUploadTypes={21 - modules.length || 1}
            disableDrop={isDragging}
            onDragDrop={(e) =>
              onDragDrop(e, {
                destination: 'carousel',
                index,
                allowMultiple: 21 - modules.length || 1,
              })
            }
            onShowAssetPicker={() =>
              onShowAssetPicker({
                destination: 'carousel',
                index,
                allowMultiple: 21 - modules.length || 1,
              })
            }
            readOnly={readOnly}
            style={{ height: notCropped ? assetHeight : 375, width: 375 }}
          />
        )}
        <div ref={footerRef} css={styles.footerWrapper}>
          <MobileStreamFooter
            accountUsername={accountUsername}
            content={content}
            contentCharacterLimit={2200}
            contentPlaceholderText="Add caption..."
            description=""
            effect=""
            modules={modules}
            index={index}
            isPresenting={isPresenting}
            onContentChange={
              onContentChange
                ? (val: string) => onContentChange(val)
                : doNothing
            }
            postType={postType}
            readOnly={readOnly}
            truncate={truncate}
          />
        </div>
      </div>
    </div>
  )
}
